import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Bot,
  Calendar,
  Clock,
  Users,
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Award,
  MessageCircle,
  Tag,
  Search,
  Focus,
  Brain,
  Mail,
  ArrowRight,
} from "lucide-react";
import { useCourseStore } from "../store/courseStore";
import { supabase } from "../lib/supabase";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";
import SearchBox from "./SearchBox";
import SEO, { generateCourseSchema, generateBreadcrumbSchema } from "./SEO";
import { Navigation } from "./Navigation";
import ContactModal from "./ContactModal";
import CalWidget from "./CalWidget";
import Footer from "./Footer";
import OptimizedImage from "./OptimizedImage";
// Removed EmailJS import - now using Netlify Forms

interface CourseDetailProps {
  previewMode?: boolean;
  slug?: string; // Make slug optional since it can come from either props or params
}

export default function CourseDetail({
  previewMode = false,
  slug: propSlug,
}: CourseDetailProps) {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentCourse, loading, error, fetchCourse } = useCourseStore();
  const [expandedFaqs, setExpandedFaqs] = useState<Record<number, boolean>>({});
  const [learningOutcomes, setLearningOutcomes] = useState<any[]>([]);
  const [outcomesLoading, setOutcomesLoading] = useState(false);
  const [outcomesError, setOutcomesError] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCalOpen, setIsCalOpen] = useState(false);

  // Use slug from props if provided, otherwise use from params
  const courseSlug = propSlug || paramSlug;

  // Check if we're in preview mode (either from props or URL)
  const isPreview = previewMode;

  useEffect(() => {
    if (courseSlug) {
      fetchCourse(courseSlug, true); // Pass true to indicate this is a slug
    }
  }, [courseSlug, fetchCourse]);

  useEffect(() => {
    async function fetchLearningOutcomes() {
      if (!currentCourse?.id) return;

      try {
        setOutcomesLoading(true);
        setOutcomesError(null);

        const { data, error } = await supabase
          .from("course_learning_outcomes")
          .select("*")
          .eq("course_id", currentCourse.id)
          .order("order_number");

        if (error) throw error;
        setLearningOutcomes(data || []);
      } catch (err) {
        console.error("Error fetching learning outcomes:", err);
        setOutcomesError("Failed to load learning outcomes");
      } finally {
        setOutcomesLoading(false);
      }
    }

    fetchLearningOutcomes();
  }, [currentCourse?.id]);

  const toggleFaq = (index: number) => {
    setExpandedFaqs((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleTagClick = (tagName: string) => {
    navigate(`/courses?tag=${encodeURIComponent(tagName)}`);
  };

  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatSessionTime = (timeString: string) => {
    // Convert "HH:MM:SS" to "HH:MM AM/PM"
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading || outcomesLoading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!currentCourse) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <p className="text-yellow-700">Course not found</p>
      </div>
    );
  }

  // Filter sessions to only show those with display_session set to true
  const displayableSessions =
    currentCourse.course_sessions?.filter(
      (session: any) => session.display_session,
    ) || [];
  const hasDisplayableSessions = displayableSessions.length > 0;

  // Generate enhanced schema for the free ChatGPT workshop
  const isFreeWorkshop = courseSlug === "free-chatgpt-workshop";

  // Create course schema with local SEO enhancements
  const courseSchema = generateCourseSchema({
    name: currentCourse.title,
    description: currentCourse.description || "",
    provider: "One80Labs - Omaha AI Training",
    url: `https://one80labs.com/course/${courseSlug}`,
    image:
      currentCourse.image_url ||
      "https://images.unsplash.com/photo-1677442135968-6d89485dd1d1",
    price: isFreeWorkshop ? 0 : currentCourse.price || 997,
    duration: currentCourse.duration || "3 hours",
  });

  // Local business schema for better local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs - AI Training in Omaha",
    image: "https://one80labs.com/logo.png",
    description:
      "The Midwest's Premier AI Agency providing AI training in Omaha, Nebraska and surrounding areas. Offering free ChatGPT workshops and comprehensive AI courses.",
    "@id": "https://one80labs.com",
    url: `https://one80labs.com/course/${courseSlug}`,
    telephone: "+1-402-915-3613",
    priceRange: isFreeWorkshop ? "Free" : "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651,
      longitude: -95.9397,
    },
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
        sameAs: "https://en.wikipedia.org/wiki/Omaha,_Nebraska",
      },
      {
        "@type": "City",
        name: "Lincoln",
        sameAs: "https://en.wikipedia.org/wiki/Lincoln,_Nebraska",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
        sameAs: "https://en.wikipedia.org/wiki/Council_Bluffs,_Iowa",
      },
      {
        "@type": "City",
        name: "Bellevue",
        sameAs: "https://en.wikipedia.org/wiki/Bellevue,_Nebraska",
      },
      {
        "@type": "City",
        name: "Papillion",
        sameAs: "https://en.wikipedia.org/wiki/Papillion,_Nebraska",
      },
      {
        "@type": "State",
        name: "Nebraska",
        sameAs: "https://en.wikipedia.org/wiki/Nebraska",
      },
      {
        "@type": "State",
        name: "Iowa",
        sameAs: "https://en.wikipedia.org/wiki/Iowa",
      },
    ],
  };

  // FAQ schema for the course
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Is the ChatGPT workshop really free?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, our 3-hour Intro to AI with ChatGPT workshop is completely free for businesses and professionals in Omaha, Council Bluffs, and throughout the Midwest. This hands-on training provides a solid foundation in AI concepts and practical skills with no cost or obligation.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need technical skills to take this AI course?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No technical background is required. This course is designed for professionals from all fields in Omaha, Nebraska and surrounding areas who want to enhance their productivity using AI tools. Basic computer skills and familiarity with office software are sufficient.",
        },
      },
      {
        "@type": "Question",
        name: "What AI tools will we be using during the workshop?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We'll primarily work with ChatGPT, and introduce other specialized AI tools depending on the specific needs of participants. All tools used either have free tiers or trial versions available, making them accessible for businesses in Omaha and throughout Nebraska and Iowa.",
        },
      },
      {
        "@type": "Question",
        name: "Will I receive any materials or resources in the free AI training?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, all participants in our free Omaha AI workshop receive a comprehensive digital workbook, prompt templates, cheat sheets for different AI tools, and access to a private community for ongoing support and learning.",
        },
      },
      {
        "@type": "Question",
        name: "Can this AI course be customized for my Omaha organization?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely! We offer customized versions of this workshop for teams and organizations in Omaha, Council Bluffs, and throughout Nebraska and Iowa, tailored to your specific industry, challenges, and goals. Contact us to discuss your needs and receive a custom proposal.",
        },
      },
    ],
  };

  // Event schema for the course sessions
  const eventSchema = hasDisplayableSessions
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: isFreeWorkshop
          ? "Free ChatGPT Workshop in Omaha"
          : currentCourse.title,
        description: currentCourse.description,
        image: currentCourse.image_url,
        startDate: displayableSessions[0]?.session_date,
        endDate: displayableSessions[0]?.session_date,
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: displayableSessions[0]?.is_virtual
          ? "https://schema.org/OnlineEventAttendanceMode"
          : "https://schema.org/OfflineEventAttendanceMode",
        location: displayableSessions[0]?.is_virtual
          ? {
              "@type": "VirtualLocation",
              url: "https://one80labs.com/virtual-classroom",
            }
          : {
              "@type": "Place",
              name: displayableSessions[0]?.location || "Omaha Location",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Omaha",
                addressRegion: "NE",
                addressCountry: "US",
              },
            },
        organizer: {
          "@type": "Organization",
          name: "One80Labs",
          url: "https://one80labs.com",
        },
        offers: {
          "@type": "Offer",
          price: isFreeWorkshop
            ? "0"
            : currentCourse.price?.toString() || "997",
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          validFrom: new Date().toISOString().split("T")[0],
        },
        performer: {
          "@type": "Person",
          name: "Nick Kramer",
          jobTitle: "AI Productivity Specialist & Technology Educator",
        },
      }
    : null;

  // Combine all schemas
  const schemas = [courseSchema, localBusinessSchema, faqSchema];
  if (eventSchema) schemas.push(eventSchema);

  return (
    <div className="min-h-screen bg-white">
      {!previewMode && (
        <SEO
          title={
            isFreeWorkshop
              ? "Free ChatGPT Workshop in Omaha, NE | One80Labs AI Training"
              : `${currentCourse.title} | One80Labs AI Training in Omaha`
          }
          description={
            isFreeWorkshop
              ? "Join our free 3-hour ChatGPT workshop in Omaha, Nebraska. Learn practical AI skills with hands-on training designed for professionals at all levels. Virtual and in-person options available."
              : currentCourse.description ||
                "Transform your skills with our comprehensive AI training courses in Omaha, Nebraska and throughout the Midwest."
          }
          image={
            currentCourse.image_url ||
            "https://images.unsplash.com/photo-1677442135968-6d89485dd1d1"
          }
          jsonLd={schemas}
          breadcrumbs={[
            { name: "Home", url: "https://one80labs.com" },
            { name: "Courses", url: "https://one80labs.com/courses" },
            {
              name: currentCourse.title,
              url: `https://one80labs.com/course/${courseSlug}`,
            },
          ]}
          keywords={
            isFreeWorkshop
              ? [
                  "free AI training Omaha",
                  "free ChatGPT workshop Nebraska",
                  "free AI course Council Bluffs",
                  "ChatGPT training Omaha",
                  "AI workshop Nebraska",
                  "free AI class Midwest",
                  "beginner AI training Omaha",
                  "business AI workshop Nebraska",
                  "free technology training Omaha",
                  "ChatGPT for beginners Nebraska",
                  "AI productivity Omaha",
                  "free professional development Nebraska",
                  "AI skills workshop Midwest",
                  "ChatGPT business applications Omaha",
                  "free tech workshop Nebraska",
                  "AI for non-technical professionals Omaha",
                  "learn ChatGPT free Nebraska",
                  "AI fundamentals Omaha",
                  "free AI education Midwest",
                  "hands-on ChatGPT training Omaha",
                ]
              : [
                  "AI training Omaha",
                  "ChatGPT course Nebraska",
                  "Gemini training Midwest",
                  "Microsoft 365 Copilot Omaha",
                  "AI workshops Nebraska",
                  "business AI training Omaha",
                  "AI certification Midwest",
                  "professional development Omaha",
                  "technology training Nebraska",
                  "AI workshops Council Bluffs",
                  "corporate AI training Omaha",
                  "AI productivity courses Nebraska",
                  "prompt engineering training Midwest",
                  "AI for business Omaha",
                  "learn AI Nebraska",
                ]
          }
        />
      )}
      <Navigation onSearchClick={() => setShowSearch(!showSearch)} />
      {showSearch && (
        <div className="absolute left-0 right-0 bg-white px-4 py-3 border-t border-gray-200 shadow-lg">
          <div className="max-w-3xl mx-auto">
            <SearchBox />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left Column - Course Content */}
          <div className="lg:w-2/3">
            <Link
              to="/courses"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to All Courses
            </Link>

            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              {currentCourse.title}
            </h1>

            {currentCourse.image_url && (
              <OptimizedImage
                src={currentCourse.image_url}
                alt={currentCourse.title}
                width={1200}
                height={256}
                hero={true}
                className="w-full h-64 object-cover rounded-lg mb-8"
              />
            )}

            <div className="prose max-w-none mb-12">
              <p className="text-gray-600">{currentCourse.description}</p>
              <div
                dangerouslySetInnerHTML={{ __html: currentCourse.content }}
              />
            </div>

            {/* Learning Outcomes */}
            {learningOutcomes.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  What You Will Learn
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {learningOutcomes.map((outcome) => (
                    <div
                      key={outcome.id}
                      className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-gray-900 mr-3 mt-0.5" />
                        <p className="text-gray-600">{outcome.outcome_text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Course Modules */}
            {currentCourse.course_modules &&
              currentCourse.course_modules.length > 0 && (
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Course Modules
                  </h2>
                  <div className="space-y-4">
                    {currentCourse.course_modules.map((module: any) => (
                      <div
                        key={module.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <h3 className="font-medium text-gray-900">
                            Module {module.order_number}: {module.title}
                          </h3>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-600 mb-3">
                            {module.description}
                          </p>
                          <div className="flex items-center text-gray-500 mb-4">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>{module.duration}</span>
                          </div>
                          <div className="space-y-2">
                            {module.module_topics?.map((topic: any) => (
                              <div key={topic.id} className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-[#F98B3D] mr-2 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-600">
                                  {topic.topic_text}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

            {/* FAQs */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {/* FAQ Item 1 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(1)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Do I need technical skills to take this course?
                    </h3>
                    {expandedFaqs[1] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[1] && (
                    <div className="p-4 text-gray-600">
                      <p>
                        No technical background is required. This course is
                        designed for professionals from all fields who want to
                        enhance their productivity using AI tools. Basic
                        computer skills and familiarity with office software are
                        sufficient.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ Item 2 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(2)}
                  >
                    <h3 className="font-medium text-gray-900">
                      What tools will we be using during the workshop?
                    </h3>
                    {expandedFaqs[2] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[2] && (
                    <div className="p-4 text-gray-600">
                      <p>
                        We'll primarily work with ChatGPT, Gemini, and Microsoft
                        365 Co-Pilot. We'll also introduce other specialized AI
                        tools depending on the specific needs of participants.
                        All tools used either have free tiers or trial versions
                        available.
                      </p>
                    </div>
                  )}
                </div>

                {/* FAQ Item 3 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(3)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Will I receive any materials or resources?
                    </h3>
                    {expandedFaqs[3] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[3] && (
                    <div className="p-4 text-gray-600">
                      <p>Yes, all participants receive:</p>
                      <ul className="mt-2 space-y-2 list-disc list-inside">
                        <li>Comprehensive digital workbook</li>
                        <li>Prompt templates and cheat sheets</li>
                        <li>Access to private community for ongoing support</li>
                        <li>Certificate of completion</li>
                        <li>Lifetime access to course updates</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* FAQ Item 4 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className="w-full bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center text-left"
                    onClick={() => toggleFaq(4)}
                  >
                    <h3 className="font-medium text-gray-900">
                      Can this course be customized for my organization?
                    </h3>
                    {expandedFaqs[4] ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaqs[4] && (
                    <div className="p-4 text-gray-600">
                      <p>
                        Absolutely! We offer customized versions of this
                        workshop for teams and organizations, tailored to your
                        specific industry, challenges, and goals. Contact us to
                        discuss your needs and receive a custom proposal.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Meet Your Instructor */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Meet Your Instructor
              </h2>
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3">
                    <OptimizedImage
                      src="https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png"
                      alt="Nick Kramer"
                      width={300}
                      height={300}
                      className="rounded-lg w-full h-auto"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Nick Kramer
                    </h3>
                    <p className="text-gray-600 italic mb-4">
                      AI Productivity Specialist & Technology Educator
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Focus className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                        <p className="text-gray-600">
                          Nick is a technology educator and AI specialist with
                          over 15 years of experience helping professionals and
                          organizations leverage emerging technologies to
                          enhance productivity and innovation.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Brain className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                        <p className="text-gray-600">
                          As the founder of One80Labs and One80Training, Nick
                          has worked with businesses across industries—ranging
                          from finance and healthcare to manufacturing and
                          tech—guiding teams in leveraging AI for automation,
                          data analysis, and workflow optimization.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Award className="h-5 w-5 text-gray-400 mt-1 mr-3" />
                        <p className="text-gray-600">
                          Certified in AI-powered product thinking and
                          human-centered design, Nick specializes in making AI
                          accessible to non-technical professionals, ensuring
                          they can harness its power for smarter decision-making
                          and productivity gains.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Course Details */}
          <div className="lg:w-1/3">
            <div className="sticky top-24">
              {/* Register Now section - only for free-chatgpt-workshop */}
              {courseSlug === "free-chatgpt-workshop" && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      Ready to Join?
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Secure your spot in our free ChatGPT workshop and start
                      your AI journey today.
                    </p>
                    <div className="flex justify-end">
                      <Link
                        to="/register"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                      >
                        Register Now
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Course Details
                  </h3>
                  <div className="space-y-4">
                    {hasDisplayableSessions && (
                      <div className="flex items-start">
                        <Calendar className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                        <div>
                          <h4 className="font-medium text-gray-900">
                            Next Sessions
                          </h4>
                          <p className="text-gray-600">June 17, 2025</p>
                          <p className="text-gray-600">9:00 AM - 12:00 PM</p>
                          <p className="text-gray-600">Omaha (Location TBD)</p>

                          <p className="text-gray-600 mt-4">June 18, 2025</p>
                          <p className="text-gray-600">1:00 PM - 4:00 PM</p>
                          <p className="text-gray-600">Omaha (Location TBD)</p>

                          <p className="text-gray-600 mt-4">June 19, 2025</p>
                          <p className="text-gray-600">9:00 AM - 12:00 PM</p>
                          <p className="text-blue-600">(Virtual)</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Participants
                        </h4>
                        <p className="text-gray-600">
                          Limited to {currentCourse.max_students} per session
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MessageCircle className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">Format</h4>
                        <p className="text-gray-600">
                          In-person or virtual options
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Clock className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">Duration</h4>
                        <p className="text-gray-600">
                          {currentCourse.duration}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Tag className="h-5 w-5 text-gray-500 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">Price</h4>
                        <p className="text-gray-600">
                          {isFreeWorkshop ? "Free" : `$${currentCourse.price}`}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 space-y-4">
                    <button
                      onClick={() => setIsContactModalOpen(true)}
                      className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Contact Us
                    </button>
                    <button
                      onClick={() => setIsCalOpen(true)}
                      className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Schedule a Call
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        // Using Netlify Forms - no onSubmit prop needed
      />

      {/* Calendar Widget Modal */}
      <CalWidget isOpen={isCalOpen} onClose={() => setIsCalOpen(false)} />

      <Footer />
    </div>
  );
}
